import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Col, Collapse, FormGroup, Input, Label, Row } from 'reactstrap';
import { ComboBox, DropdownBox } from '../../../dropdowns';
import moment from 'moment';
import api from "../../../../services/api";
import _ from 'lodash';
import { IndeterminateCheckbox } from '../../../react-table/controls';
import { DatePicker } from '../../../date-pickers';
import { formatDate } from '../../../../utils';
import { FileDropzone, MultipleDropzoneList } from '../../../FileDropzone';
import fileDownload from 'js-file-download';
import { TooltipButton } from '../../../inputs';

const apiUrl = "employeechangeconditionv2";

export default function ECCBaseForm(props) {
    const {
        isApproval,
        shouldReload = true,
        onLoadData,
        approvalData,
        lookups,
        setLookups,
        itemInEdit,
        setItemInEdit,
        documents,
        setDocuments,
        validationObj,
        setLoading,
        setShouldCheckApprover,
        selectedAllocationHistoryRows,
        footer,
    } = props;

    const [secondmentChecked, setSecondmentChecked] = useState(itemInEdit?.secondmentProjectId ? true : false);
    const [allowanceChecked, setAllowanceChecked] = useState(itemInEdit?.eccAllowances?.length > 0 ? true : false);
    const [workSequenceChecked, setWorkSequenceChecked] = useState(itemInEdit?.workSequence ? true : false);

    useEffect(() => {
        setSecondmentChecked(itemInEdit?.secondmentProjectId ? true : false);
        setAllowanceChecked(itemInEdit?.eccAllowances?.length > 0 ? true : false);
        setShouldCheckApprover(true);
    }, [itemInEdit?.employeeChangeConditionId]);

    useEffect(() => {
        if (!itemInEdit?.employeeChangeConditionId || !lookups?.workSequences)
            return;

        if (!itemInEdit.workSequence)
        {
            setWorkSequenceChecked(false);
            return;
        }
        
        const selectedWorkSequence = lookups.workSequences.filter(w => w.lookupValue === itemInEdit.workSequence)[0];

        if (!selectedWorkSequence && itemInEdit.workSequence)
        {
            setWorkSequenceChecked(true);
            setItemInEdit({
                ...itemInEdit,
                workSequenceObj: lookups.workSequences.filter(w => w.lookupValue === "Other")[0],
            });
            return;
        }

        setWorkSequenceChecked(true);
        setItemInEdit({
            ...itemInEdit,
            workSequenceObj: selectedWorkSequence,
        });
    }, [itemInEdit?.employeeChangeConditionId, lookups?.workSequences]);

    const loadData = useCallback(() => {
        setLoading(true);

        const apiCalls = [
            api.get("lookup/recruitmenttype"),
            api.get("lookup/employmenttype"),
            api.get("lookup/eccallowancetype"),
            api.get("lookup/eccstatustype"),
            api.get(`/lookup/pointofhire`),
            api.get(`/lookup/salaryoccurencetype`),
            api.get(`/lookup/worksequence`),
        ];

        Promise.all(apiCalls)
            .then((responses) => {
                setLookups({
                    recruitmentTypes: responses[0].data,
                    employmentTypes: responses[1].data,
                    eccAllowanceTypes: responses[2].data,
                    eccStatusTypes: responses[3].data,
                    pointOfHires: responses[4].data,
                    salaryOccurenceTypes: responses[5].data,
                    workSequences: [...responses[6].data, {lookupId: -1, lookupValue: "Other"}],
                });

                if (onLoadData)
                    onLoadData();
            })
            .catch((error) => console.error({error}))
            .finally(() => setLoading(false))
        ;
    }, [onLoadData]);

    useEffect(() => {
        if (!shouldReload)
            return;

        loadData();
    }, [shouldReload]);

    const handleTextChanged = useCallback(({ target: { name, value } }, callback = null) => {
        const newItemInEdit = JSON.parse(JSON.stringify(itemInEdit));

        if (callback)
            callback(newItemInEdit);

        setItemInEdit({ ...newItemInEdit, [name]: value });
    }, [itemInEdit]);

    const handleDateChanged = useCallback((name, value) => {
        setItemInEdit({ ...itemInEdit, [name]: value });
    }, [itemInEdit]);

    const downloadAttachment = (fileInfo) => {
        if (fileInfo.file) {
            fileDownload(fileInfo.file, fileInfo.file.name);
            return;
        }
        
        setLoading(true);
        const url = `${apiUrl}/document/${fileInfo.eccDocumentId}`;
        api.get(url, { responseType: 'blob' })
            .then(blob => {
                fileDownload(blob.data, fileInfo.filename);
            })
            .catch(error => {
                console.error(error)
            })
            .finally(() => setLoading(false))
        ;
    }

    const removeAttachment = useCallback((fileInfo) => {
        const newAttachments = [...documents];
        const removedItem = newAttachments.filter(a => a.eccDocumentId === fileInfo.eccDocumentId)[0];
        const removedIndex = newAttachments.indexOf(removedItem);
        newAttachments.splice(removedIndex, 1);
        setDocuments([...newAttachments]);
    }, [documents]);

    const files = useMemo(() => {
        if (documents?.length > 0)
            return <MultipleDropzoneList
                idField="eccDocumentId"
                nameField="filename"
                sizeField="fileLength"
                download={downloadAttachment}
                remove={removeAttachment}
                data={documents}
                badgeTextField="approverRole"
            />;

        return null;
    }, [documents]);

    const downloadToolOfTradeAttachment = () => {
        setLoading(true);
        const url = `${apiUrl}/tooloftrade/document`;
        api.get(url)
            .then(response => {
                response.data.forEach(file => {
                    const byteCharacters = atob(file.base64Content);
                    const byteNumbers = new Array(byteCharacters.length);

                    for (let i = 0; i < byteCharacters.length; i++)
                        byteNumbers[i] = byteCharacters.charCodeAt(i);

                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: file.contentType });
                    fileDownload(blob, file.fileName);
                });
            })
            .catch(error => {
                console.error(error)
            })
            .finally(() => setLoading(false))
        ;
    }

    const downloadSalaryAttachment = () => {
        setLoading(true);
        const url = `${apiUrl}/salary/document`;
        api.get(url)
            .then(response => {
                response.data.forEach(file => {
                    const byteCharacters = atob(file.base64Content);
                    const byteNumbers = new Array(byteCharacters.length);

                    for (let i = 0; i < byteCharacters.length; i++)
                        byteNumbers[i] = byteCharacters.charCodeAt(i);

                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: file.contentType });
                    fileDownload(blob, file.fileName);
                });
            })
            .catch(error => {
                console.error(error)
            })
            .finally(() => setLoading(false))
        ;
    }

    return (
        <>
            <Collapse id="validationAlert" isOpen={validationObj?.length > 0} style={{scrollMargin: "100px"}} className="my-2">
                <Row>
                    <Col sm={12}>
                        <Alert color="warning" className="pa-0 d-flex justify-content-between">
                            <span className="alert-text">
                                <ul className="mb-0">
                                {
                                    validationObj.map((item, index) => <li key={`alert-${index}`}>{item.message}</li>)
                                }
                                </ul>
                            </span>
                        </Alert>
                    </Col>
                </Row>
            </Collapse>
            <Row className="m-0 p-0">
                <Col lg={6} md={12} className="m-0 p-0 pr-2">
                    <Row>
                        <Col lg={4}></Col>
                        <Col lg={4}>
                            <Label><b>Current</b></Label>
                        </Col>
                        <Col lg={4}>
                            <Label><b>Proposed</b></Label>
                        </Col>
                    </Row>
                    <Row className="mt-1">
                        <Col lg={4}>
                            <Label>Project<span className="text-danger">*</span></Label>
                        </Col>
                        <Col lg={4}>
                            <Label>{itemInEdit?.project?.projectName ?? "N/A"}</Label>
                        </Col>
                        <Col lg={4}></Col>
                    </Row>
                    <Row className="mt-1">
                        <Col lg={4}>
                            <Label>Position<span className="text-danger">*</span></Label>
                        </Col>
                        <Col lg={4}>
                            <Label>{itemInEdit?.position?.positionName ?? "N/A"}</Label>
                        </Col>
                        <Col lg={4}></Col>
                    </Row>
                    <Row className="mt-1">
                        <Col lg={4}>
                            <Label>Recruitment Type<span className="text-danger">*</span></Label>
                        </Col>
                        <Col lg={4}>
                            <Label>N/A</Label>
                        </Col>
                        <Col lg={4}>
                            <DropdownBox
                                data={lookups.recruitmentTypes}
                                selectedItem={itemInEdit.proposedRecruitmentType}
                                onChange={(item) => {
                                    const newItemInEdit = {...itemInEdit};

                                    if (item?.lookupId === newItemInEdit?.proposedRecruitmentTypeId)
                                        return;

                                    setItemInEdit({ ...newItemInEdit, proposedRecruitmentType: item, proposedRecruitmentTypeId: item?.lookupId ?? null });
                                    setShouldCheckApprover(true);
                                }}
                                disabled={isApproval || itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                    <Row className="mt-1">
                        <Col lg={4}>
                            <Label>Employment Basis<span className="text-danger">*</span></Label>
                        </Col>
                        <Col lg={4}>
                            <Label>{itemInEdit?.currentEmploymentTypeName ?? "N/A"}</Label>
                        </Col>
                        <Col lg={4}>
                            <DropdownBox
                                data={lookups.employmentTypes}
                                selectedItem={itemInEdit.proposedEmploymentType}
                                onChange={(item) => {
                                    const newItemInEdit = {...itemInEdit};

                                    if (item?.lookupId === newItemInEdit?.proposedEmploymentTypeId)
                                        return;

                                    setItemInEdit({ ...newItemInEdit, proposedEmploymentType: item, proposedEmploymentTypeId: item?.lookupId ?? null });
                                }}
                                disabled={itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                {
                    selectedAllocationHistoryRows[0]?.original?.endDate ?
                    <Row className="mt-1">
                        <Col lg={4}>
                            <Label>End Date</Label>
                        </Col>
                        <Col lg={4}>
                            <Label>{itemInEdit.currentEndDate ? moment(itemInEdit.currentEndDate).format("DD/MM/YYYY") : "N/A"}</Label>
                        </Col>
                        <Col lg={4}>
                            <DatePicker
                                name="proposedEndDate"
                                id="proposedEndDate"
                                value={itemInEdit.proposedEndDate ? formatDate(itemInEdit.proposedEndDate) : ""}
                                onChange={value => handleDateChanged("proposedEndDate", !value ? null : moment(value).format("YYYY-MM-DDT00:00:00"))}
                                closeOnSelect
                                isValidDate={(current) => {
                                    const earliest = moment().subtract( 14, 'day' );
                                    return current.isAfter(earliest);
                                }}
                                className="mb-0"
                                disabled={itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                    :
                    null
                }
                    <Row className="mt-1">
                        <Col lg={4}>
                            <Label>Point of Hire</Label>
                        </Col>
                        <Col lg={4}>
                            <Label>N/A</Label>
                        </Col>
                        <Col lg={4}>
                            <DropdownBox
                                data={lookups.pointOfHires ?? []}
                                selectedItem={itemInEdit.proposedPointOfHire}
                                onChange={(item) => {
                                    const newItemInEdit = {...itemInEdit};

                                    if (item?.lookupId === newItemInEdit?.proposedPointOfHireId)
                                        return;

                                    setItemInEdit({ ...newItemInEdit, proposedPointOfHire: item, proposedPointOfHireId: item?.lookupId ?? null });
                                }}
                                disabled={itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                {
                    itemInEdit?.proposedRecruitmentType?.lookupValue.toLowerCase().includes("salaried") ?
                    <Row className="mt-1">
                        <Col lg={4}>
                            <Label>Salary</Label>
                        </Col>
                        <Col lg={4}>
                            <Label>N/A</Label>
                        </Col>
                        <Col lg={4}>
                            <Input
                                name="salary"
                                id="salary"
                                type="number"
                                value={itemInEdit.salary ?? ""}
                                onChange={handleTextChanged}
                                autoComplete="off"
                                disabled={itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                    :
                    null
                }
                </Col>
                <Col lg={6} md={12} className="m-0 p-0 pl-2">
                    <Row className="mt-2">
                        <Col lg={12}>
                            <IndeterminateCheckbox
                                id="secondmentChecked"
                                customTitle="Secondment"
                                onChange={() => {
                                    const currentState = secondmentChecked;
                                    setSecondmentChecked(!secondmentChecked);

                                    if (!currentState)
                                        return;

                                    setItemInEdit({
                                        ...itemInEdit,
                                        secondmentProjectId: null,
                                        secondmentProject: null,
                                        secondmentEndDate: null,
                                    });
                                }}
                                checked={secondmentChecked}
                                disabled={isApproval || itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                    <Row className="px-4">
                        <Col lg={4}>
                            <FormGroup>
                                <Label>
                                    Project
                                {
                                    secondmentChecked ?
                                    <span className="text-danger">*</span>
                                    :
                                    null
                                }
                                </Label>
                                <ComboBox
                                    endpoint="/project/search"
                                    isLookup={false}
                                    idField="projectId"
                                    valueField="projectName"
                                    minLength={3}
                                    selectedItem={itemInEdit.secondmentProject}
                                    onChange={(item) => {
                                        const newItemInEdit = {...itemInEdit};

                                        if (item?.projectId === newItemInEdit?.secondmentProjectId)
                                            return;

                                        setItemInEdit({ ...newItemInEdit, secondmentProject: item, secondmentProjectId: item?.projectId ?? null });
                                        setShouldCheckApprover(true);
                                    }}
                                    disabled={!secondmentChecked || isApproval || itemInEdit.statusName === "Approved"}
                                    additionalParam={{
                                        excludeHiddenProjects: true,
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={4}>
                            <FormGroup>
                                <Label>
                                    Start Date
                                {
                                    secondmentChecked ?
                                    <span className="text-danger">*</span>
                                    :
                                    null
                                }
                                </Label>
                                <DatePicker
                                    name="secondmentStartDate"
                                    id="secondmentStartDate"
                                    value={itemInEdit.secondmentStartDate ? formatDate(itemInEdit.secondmentStartDate) : ""}
                                    onChange={value => handleDateChanged("secondmentStartDate", !value ? null : moment(value).format("YYYY-MM-DDT00:00:00"))}
                                    closeOnSelect
                                    isValidDate={(current) => {
                                        const earliest = moment().subtract( 14, 'day' );
                                        return current.isAfter(earliest);
                                    }}
                                    className="mb-0"
                                    disabled={!secondmentChecked || itemInEdit.statusName === "Approved"}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={4}>
                            <FormGroup>
                                <Label>
                                    End Date
                                {
                                    secondmentChecked ?
                                    <span className="text-danger">*</span>
                                    :
                                    null
                                }
                                </Label>
                                <DatePicker
                                    name="secondmentEndDate"
                                    id="secondmentEndDate"
                                    value={itemInEdit.secondmentEndDate ? formatDate(itemInEdit.secondmentEndDate) : ""}
                                    onChange={value => handleDateChanged("secondmentEndDate", !value ? null : moment(value).format("YYYY-MM-DDT00:00:00"))}
                                    closeOnSelect
                                    isValidDate={(current) => {
                                        const earliest = moment().subtract( 14, 'day' );
                                        return current.isAfter(earliest);
                                    }}
                                    className="mb-0"
                                    disabled={!secondmentChecked || itemInEdit.statusName === "Approved"}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col lg={12}>
                            <IndeterminateCheckbox
                                id="allowanceChecked"
                                customTitle="Allowance"
                                onChange={() => {
                                    const currentState = allowanceChecked;
                                    setAllowanceChecked(!allowanceChecked);

                                    if (!currentState)
                                        return;

                                    setItemInEdit({
                                        ...itemInEdit,
                                        eccAllowances: [],
                                    });
                                }}
                                checked={allowanceChecked}
                                disabled={itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                    <Row className="mt-2 px-2">
                        <Col lg={4}>
                            <Label><b>Type</b></Label>
                        </Col>
                        <Col lg={4}>
                            <Label><b>Value</b></Label>
                        </Col>
                        <Col lg={4}>
                            <Label><b>Occurence</b></Label>
                        </Col>
                    </Row>
                {
                    lookups.eccAllowanceTypes?.map((e, idx) => {
                        return (
                            <Row key={`allowance_${idx}`} className={`${idx === lookups.eccAllowanceTypes?.length - 1 ? "mt-1 mb-2 pb-2" : "my-1"} px-4`}>
                                <Col lg={4} className="d-flex flex-wrap justify-content-start align-items-center">
                                    <IndeterminateCheckbox
                                        id={`allowance_${idx}_checkbox`}
                                        customTitle={e.lookupLabel}
                                        onChange={() => {
                                            if (!itemInEdit)
                                                return;

                                            const newItemInEdit = JSON.parse(JSON.stringify(itemInEdit));
                                            const currentAllowance = newItemInEdit.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)[0];

                                            if (!currentAllowance)
                                            {
                                                if (!newItemInEdit.eccAllowances)
                                                    newItemInEdit.eccAllowances = [];

                                                newItemInEdit.eccAllowances.push({
                                                    eccAllowanceId: 0,
                                                    employeeChangeConditionId: itemInEdit.employeeChangeConditionId ?? 0,
                                                    allowanceTypeId: e.lookupId,
                                                    allowanceOccurenceId: null,
                                                    allowance: null,
                                                });
                                                setItemInEdit({...newItemInEdit});
                                                return;
                                            }

                                            const currentAllowanceIndex = newItemInEdit.eccAllowances.indexOf(currentAllowance);
                                            newItemInEdit.eccAllowances.splice(currentAllowanceIndex, 1);
                                            setItemInEdit({...newItemInEdit});
                                        }}
                                        checked={itemInEdit?.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)?.length > 0}
                                        disabled={!allowanceChecked || itemInEdit.statusName === "Approved"}
                                    />
                                </Col>
                                <Col lg={4}>
                                    <Input
                                        name={`allowance_${idx}_input`}
                                        id={`allowance_${idx}_input`}
                                        value={itemInEdit?.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)[0]?.allowance ?? ""}
                                        autoComplete="off"
                                        type="number"
                                        onChange={(event) => {
                                            const newItemInEdit = JSON.parse(JSON.stringify(itemInEdit));
                                            const currentAllowance = newItemInEdit.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)[0];

                                            if (!currentAllowance)
                                                return;

                                            currentAllowance.allowance = event.target.value;
                                            setItemInEdit({...newItemInEdit});
                                        }}
                                        disabled={!itemInEdit?.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)?.length || itemInEdit.statusName === "Approved"}
                                    />
                                </Col>
                                <Col lg={4}>
                                    <DropdownBox
                                        data={lookups.salaryOccurenceTypes}
                                        selectedItem={itemInEdit?.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)[0]?.allowanceOccurence}
                                        idField="lookupId"
                                        valueField="lookupValue"
                                        disableClear={true}
                                        onChange={(event) => {
                                            const newItemInEdit = JSON.parse(JSON.stringify(itemInEdit));
                                            const currentAllowance = newItemInEdit.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)[0];

                                            if (!currentAllowance)
                                                return;

                                            currentAllowance.allowanceOccurenceId = event.lookupId;
                                            currentAllowance.allowanceOccurence = event;
                                            setItemInEdit({...newItemInEdit});
                                        }}
                                        disabled={!itemInEdit?.eccAllowances?.filter(a => a.allowanceTypeId === e.lookupId)?.length || itemInEdit.statusName === "Approved"}
                                    />
                                </Col>
                            </Row>
                        );
                    })
                }
                    <Row className="mt-2">
                        <Col lg={12}>
                            <IndeterminateCheckbox
                                id="workSequenceChecked"
                                customTitle="Roster"
                                onChange={() => {
                                    const currentState = workSequenceChecked;
                                    setWorkSequenceChecked(!workSequenceChecked);

                                    if (!currentState)
                                        return;

                                    setItemInEdit({
                                        ...itemInEdit,
                                        workSequence: null,
                                        workSequenceObj: null,
                                    });
                                }}
                                checked={workSequenceChecked}
                                disabled={itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                    <Row className="px-4">
                        <Col lg={6}>
                            <FormGroup>
                                <Label>
                                    Template
                                {
                                    workSequenceChecked ?
                                    <span className="text-danger">*</span>
                                    :
                                    null
                                }
                                </Label>
                                <DropdownBox
                                    data={lookups.workSequences}
                                    selectedItem={itemInEdit?.workSequenceObj}
                                    idField="lookupId"
                                    valueField="lookupValue"
                                    disableClear={true}
                                    onChange={(event) => {
                                        const newItemInEdit = JSON.parse(JSON.stringify(itemInEdit));
                                        newItemInEdit.workSequenceObj = event;
                                        newItemInEdit.workSequence = event?.lookupValue ?? null;

                                        if (newItemInEdit.workSequence === "Other")
                                            newItemInEdit.workSequence = null;

                                        setItemInEdit({...newItemInEdit});
                                    }}
                                    disabled={!workSequenceChecked || itemInEdit.statusName === "Approved"}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={6}>
                            <FormGroup>
                                <Label>
                                    Custom Value
                                {
                                    itemInEdit?.workSequenceObj?.lookupValue === "Other" ?
                                    <span className="text-danger">*</span>
                                    :
                                    null
                                }
                                </Label>
                                <Input
                                    name="workSequence"
                                    id="workSequence"
                                    value={itemInEdit.workSequenceObj?.lookupValue !== "Other" ? "" : (itemInEdit.workSequence ?? "")}
                                    onChange={handleTextChanged}
                                    autoComplete="off"
                                    disabled={!itemInEdit?.workSequenceObj || itemInEdit?.workSequenceObj?.lookupValue !== "Other" || itemInEdit.statusName === "Approved"}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mt-2">
                        <Col lg={12}>
                            <IndeterminateCheckbox
                                id="isToolOfTrade"
                                customTitle="Tool of Trade"
                                onChange={() => {
                                    setItemInEdit({
                                        ...itemInEdit,
                                        isToolOfTrade: itemInEdit?.isToolOfTrade !== undefined ? !itemInEdit.isToolOfTrade : true,
                                    });
                                }}
                                checked={itemInEdit?.isToolOfTrade ?? false}
                                disabled={itemInEdit.statusName === "Approved"}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className="m-0 p-0">
                <Col lg={12} className="m-0 p-0">
                    <Row className="m-0 p-0">
                        <Col lg={12} className="m-0 p-0">
                            <FormGroup>
                                <Label>
                                    Justification<span className="text-danger">*</span>
                                </Label>
                                <Input
                                    name="notes"
                                    id="notes"
                                    type="textarea"
                                    required
                                    value={itemInEdit.notes ?? ""}
                                    onChange={handleTextChanged}
                                    rows={5}
                                    disabled={itemInEdit.statusName === "Approved"}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={12} className="m-0 p-0">
                            <FormGroup>
                                <Label>
                                    {isApproval ? (approvalData?.approverRole === "Managing Director" ? "Red Zone" : (approvalData?.approverRole === "HR Director Team" ? "Tool of Trade" : "Supporting")) : "Supporting"} Documentation<span className="text-danger">*</span>
                                </Label>
                            {
                                itemInEdit?.isToolOfTrade ?
                                <TooltipButton
                                    id="download_toolOfTrade"
                                    title="Tool of Trade Templates"
                                    className="btn-icon ml-2"
                                    color="default"
                                    size="sm"
                                    type="button"
                                    outline
                                    onClick={() => downloadToolOfTradeAttachment()}
                                >
                                    <i className="fas fa-file-download mr-2" />Tool of Trade Template
                                </TooltipButton>
                                :
                                null
                            }
                            {
                                itemInEdit?.salary ?
                                <TooltipButton
                                    id="download_salary"
                                    title="Salary Template"
                                    className="btn-icon ml-2"
                                    color="default"
                                    size="sm"
                                    type="button"
                                    outline
                                    onClick={() => downloadSalaryAttachment()}
                                >
                                    <i className="fas fa-file-download mr-2" />Salary Template
                                </TooltipButton>
                                :
                                null
                            }
                                <FileDropzone
                                    files={files}
                                    onDrop={([file]) => {
                                        const newAttachments = documents?.length ? [...documents] : [];
                                        const newIds = newAttachments?.filter(a => a.eccDocumentId <= 0);
                                        if (newIds.length)
                                            newIds.sort((a, b) => parseInt(b.eccDocumentId) - parseInt(a.eccDocumentId));

                                        const newId = newIds.length ? newIds[0].eccDocumentId - 1 : 0;
                                        const newData = {
                                            employeeChangeConditionId: itemInEdit.employeeChangeConditionId ?? 0,
                                            eccDocumentId: newId,
                                            file
                                        };
                                        newAttachments.push(newData);
                                        setDocuments([...newAttachments]);
                                    }}
                                    maxFiles={0}
                                    disabled={itemInEdit.statusName === "Approved"}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </Col>
            </Row>
        {
            footer
        }
        </>
    );
};